import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route,
} from "react-router-dom";
import { csvParse } from 'd3-dsv';
import { group } from 'd3-array';

import Header from './Header';
import About from './About';
import Intro from './Intro';
import SankeyPage from './SankeyPage';
import { generateKey } from './Sankey';
import Footer from './Footer';
import mapToNest from './mapToNest.js';
import { LanguageContext, localize, isReady } from './Localization';

import './App.scss';

const schoolSites = `${process.env.PUBLIC_URL}/ravenswood-ssb.csv`;
const centralSupports = `${process.env.PUBLIC_URL}/ravenswood-csa.csv`;

function formatCSV(text) {
  const textByLine = text.split('\n');
  const headerIndex = textByLine.findIndex(d => d.includes('Total Costs'));
  const cleanedText = textByLine.slice(headerIndex).join('\n');
  const csv = csvParse(cleanedText)
    .map(d => {
      d.selectionKeys = [
        generateKey(d['Resource / Grant']),
        generateKey(d['School Site/Department']),
        generateKey(d['Activity']),
      ].filter(d => d !== '');
      d['Total Costs'] = +(d['Total Costs'].trim().replace(/[^0-9]/g, ''));
      return d;
    }).filter(d => d.selectionKeys.length);
  return csv;
}

export default function App() {
  const [schoolData, setSchoolData] = useState([]);
  const [centralData, setCentralData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [language, setLanguage] = useState('english')

  useEffect(() => {
    window.fetch(schoolSites)
      .then(value => value.text())
      .then(text => {
        const csv = formatCSV(text);
        setSchoolData(csv);
      });

    window.fetch(centralSupports)
      .then(value => value.text())
      .then(text => {
        const csv = formatCSV(text);
        setCentralData(csv);
      });
  }, []);

  useEffect(() => {
    if (schoolData.length && centralData.length) {
      const combinedData = schoolData.concat(centralData);
      const summaryData = mapToNest(group(combinedData, d => d['Central / SSBC / Schools / Out of District']))
        .map(d => {
          d.value = d.values.map(v => v['Total Costs']).reduce((a, v) => a + v, 0);
          return d;
        });
      setSummaryData(summaryData);

      const totalValue = summaryData.map(d => d.value).reduce((a, v) => a + v, 0);
      setTotalValue(totalValue);
    }
  }, [schoolData, centralData]);

  return !isReady() ? null : (
    <LanguageContext.Provider value={language}>
      <div className="App">
        {
          // <>
          //   <div className='guide left' />
          //   <div className='guide right' />
          // </>
        }
        <Router basename={process.env.REACT_APP_PATHNAME}>
          <Header setLanguage={setLanguage} />
          <Switch>
            <Route key='About' path='/About'>
              <About />
            </Route>
            <Route key='SchoolSiteBudgets' path='/SchoolSiteBudgets'>
              <SankeyPage
                data={schoolData}
                id='School Site Budgets'
                title={localize(language, 'school site budgets', 'title')}
                description={(
                  <div>
                    <p>
                      {localize(language, 'school site budgets', 'description')}
                    </p>
                    <h3>
                      {localize(language, 'school site budgets', 'funding title')}
                    </h3>
                    <p>
                      {localize(language, 'school site budgets', 'funding description')}
                    </p>
                  </div>
                )}
              />
            </Route>
            <Route key='CentralSupportsandAdmin' path='/CentralSupportsandAdmin'>
              <SankeyPage
                data={centralData}
                id='Central Supports and Admin'
                title={localize(language, 'central supports and admin', 'title')}
                description={(
                  <div>
                    <p>
                      {localize(language, 'central supports and admin', 'description')}
                    </p>
                  </div>
                )}
              />
            </Route>
            <Route key='/' path='/'>
              <Intro
                totalValue={totalValue}
                summaryData={summaryData}
              />
            </Route>
          </Switch>
          <Footer />
        </Router>
      </div>
    </LanguageContext.Provider>
  );
}
